import React, { useMemo, useState } from 'react';
import './PortfolioProjectsDetailContent.scss';
import { PortfolioSimpleIndicatorTable } from '../PortfolioSimpleIndicatorTable/PortfolioSimpleIndicatorTable';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectsSimpleTableIndicator } from '../../../redux/actions/indicatorProjects';
import Indicator from '../../../models/Indicator';
import { getDynamicTableFormat } from '../../../services/util';
import { IndicatorKeyEnum } from '../../../models/IndicatorKeyEnum';
import SummaryHeaderSingleValue from '../SummaryHeaderSingleValue/SummaryHeaderSingleValue';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { sortTablesByType } from '../../../services/sortsUtil';
import Filter from '../../Portfolio/Filter/Filter';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { useTranslation } from 'react-i18next';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import ErrorMessageView from '../../Util/ErrorMessage/ErrorMessageView';

const customValueStyle = { fontSize: 36 };
const customSubtilteStyle = { fontSize: 16, fontWeight: 300, color: '#264377', paddingTop: 10 };

interface ReceivedProps {
  currentIndicator: Indicator;
  indicatorKey: string;
}

type Props = ReceivedProps;

enum indicatorType {
  PPE = 'PROJECTS_PENDING_DISBURSEMENT_ELIGIBILITY',
  PDPA = 'DISBURSEMENT_PROJECTION_CURRENT_YEAR_INCLUDING_REGIONALS',
  PSD = 'PROJECTS_OVERALL_STAGE_CODE_DISBURSING_NO_DISBURSEMENTS_OVER_180_DAYS',
  PEMICI = 'PROJECTS_IN_EXECUTION_WITH_MICI_OPEN_CASES',
}

//Función usada por indicador PROJECTS_PENDING_SIGNATURE_OR_LEGISLATIVE_APPROVAL
const handleBlueBoxValues = (currentIndicator, projectTypeFilterOnDetail, getSignature) => {
  const {
    loanBlueBoxSignature,
    loanBlueBoxRatification,
    tcpBlueBoxSignature,
    tcpBlueBoxRatification,
    igrBlueBoxSignature,
    igrBlueBoxRatification,
  } = currentIndicator.blueBoxValues;
  if (projectTypeFilterOnDetail === ProjectProductTypes.Loan) {
    return getSignature ? loanBlueBoxSignature : loanBlueBoxRatification;
  } else if (projectTypeFilterOnDetail === ProjectProductTypes.CT) {
    return getSignature ? tcpBlueBoxSignature : tcpBlueBoxRatification;
  } else {
    return getSignature ? igrBlueBoxSignature : igrBlueBoxRatification;
  }
};

export const PortfolioProjectsDetailContent = (props: Props) => {
  const { t } = useTranslation();
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const { currentIndicator, indicatorKey } = props;
  const indicatorProjects = useSelector<any, any[]>(state => state.indicatorProjects);
  const { projectTypeFilterOnDetail } = useSelector<any, any>(state => state.indicatorDetailFilter);
  const dispatch = useDispatch();

  //Filter
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalProjects, setTotalProjects] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  const projectsToShow = useMemo(() => {
    const key = currentIndicator.indicatorKey;

    if (key === IndicatorKeyEnum.PROJECTS_PENDING_DISBURSEMENT_ELIGIBILITY) {
      return indicatorProjects;
    }
    if (
      key === IndicatorKeyEnum.DISBURSEMENT_PROJECTION_CURRENT_YEAR_INCLUDING_REGIONALS ||
      key === IndicatorKeyEnum.PROJECTS_OVERALL_STAGE_CODE_DISBURSING_NO_DISBURSEMENTS_OVER_180_DAYS
    ) {
      return sortTablesByType([...indicatorProjects], true, 'code', 'string');
    }

    return indicatorProjects;
  }, [indicatorProjects, currentIndicator.indicatorKey]);

  const projectTypeFilterChange = {
    ...filterIndicators,
    common: { ...filterIndicators.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async (indicatorKey: string) => {
    dispatch(setLoading(true));
    setIsLoading(true);
    setLoadingIndicator(true);
    setHasError(false);
    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
        indicatorKey,
      );
      setTotalProjects(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      setHasError(true);
      console.error(err, 'Error fetching data from indicators');
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
      setLoadingIndicator(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan], indicatorKey) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    if (indicatorKey === indicatorType.PPE) {
      dispatch(
        setCurrentIndicator(
          sendEndSummary?.find(data => data.indicatorKey === 'PROJECTS_PENDING_DISBURSEMENT_ELIGIBILITY'),
        ),
      );
    } else if (indicatorKey === indicatorType.PDPA) {
      dispatch(setCurrentIndicator(sendEndSummary?.find(data => data.id === 7)));
    } else if (indicatorKey === indicatorType.PSD) {
      dispatch(
        setCurrentIndicator(
          sendEndSummary?.find(
            data => data.indicatorKey === 'PROJECTS_OVERALL_STAGE_CODE_DISBURSING_NO_DISBURSEMENTS_OVER_180_DAYS',
          ),
        ),
      );
    } else if (indicatorKey === indicatorType.PEMICI) {
      dispatch(
        setCurrentIndicator(
          sendEndSummary?.find(data => data.indicatorKey === 'PROJECTS_IN_EXECUTION_WITH_MICI_OPEN_CASES'),
        ),
      );
    }
    setIsLoading(false);
  };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  React.useEffect(
    () => {
      getFilteredSimpleIndicators(
        dispatch,
        currentIndicator.id,
        currentIndicator.indicatorKey,
        setLoadingIndicator,
        setHasError,
        // projectTypeFilterChange,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentIndicator.id, dispatch, projectTypeFilterOnDetail, filterIndicators.common],
  );

  React.useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange(indicatorKey);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  React.useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0
    ) {
      handleCommonFilterChange(indicatorKey);
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  const handleRetry = () => {
    getFilteredSimpleIndicators(
      dispatch,
      currentIndicator.id,
      currentIndicator.indicatorKey,
      setLoadingIndicator,
      setHasError,
      // projectTypeFilterChange,
    );
  };

  return (
    <>
      {indicatorKey === IndicatorKeyEnum.PROJECTS_PENDING_SIGNATURE_OR_LEGISLATIVE_APPROVAL && (
        <div className="portfolio-projects-detail-content-header">
          <SummaryHeaderSingleValue
            value={handleBlueBoxValues(currentIndicator, projectTypeFilterOnDetail, true)}
            title={'pending_signing_of_contracts'}
            customValueStyle={customValueStyle}
            customSubtilteStyle={customSubtilteStyle}
          />
          <SummaryHeaderSingleValue
            value={handleBlueBoxValues(currentIndicator, projectTypeFilterOnDetail, false)}
            title={'pending_legislative_ratification'}
            customValueStyle={customValueStyle}
            customSubtilteStyle={customSubtilteStyle}
          />
        </div>
      )}
      <div className="portfolio-projects-detail-content-container">
        <div className={indicatorProjects.length === 0 ? 'wrapper' : 'wrapper adjust-height'}>
          {hasError ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}> {/* //add scss */}
                <ErrorMessageView
                  title={t('title_error_message_view')}
                  description={t('desc_error_message_tab')}
                  reload={handleRetry}
                  isIndicator={true}
                />
              </div>
            </>
          ) : (
            <PortfolioSimpleIndicatorTable
              projects={projectsToShow}
              tableInfo={getDynamicTableFormat(indicatorKey)}
              indicatorKey={indicatorKey}
              loading={loadingIndicator}
            />
          )}
        </div>
      </div>
    </>
  );
};

export const getFilteredSimpleIndicators = (
  d,
  currentIndicatorId,
  currentIndicatorKey,
  setLoading,
  setError,
  projectTypeFilterChange?,
) => {
  setLoading(true);
  setError(false);
  return d(getProjectsSimpleTableIndicator(currentIndicatorId, currentIndicatorKey, projectTypeFilterChange))
    .then(() => {
      setLoading(false);
    })
    .catch(error => {
      setLoading(false);
      setError(true);
    });
};
